/**
 * Federn nach Apple-Art: zwei Parameter statt Physik-Tripel.
 * `response` = Sekunden bis zum Ziel, `damping` = Dämpfungsgrad (1 = kein Überschwingen).
 * Eine Feder startet immer vom aktuellen Wert und übernimmt die Geschwindigkeit,
 * deshalb lässt sie sich jederzeit unterbrechen und umlenken.
 */
import { useEffect, useRef, useState } from "react";

export type FederOptionen = { response?: number; damping?: number };

const MAX_SCHRITT = 1 / 30;

export function reduzierteBewegung(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export class Feder {
  wert: number;
  ziel: number;
  geschwindigkeit = 0;
  private stiffness: number;
  private dampingKoeff: number;
  private raf = 0;
  private zuletzt = 0;
  private onUpdate: (wert: number) => void;
  private onSettle?: () => void;

  constructor(start: number, onUpdate: (wert: number) => void, opt: FederOptionen = {}) {
    this.wert = start;
    this.ziel = start;
    this.onUpdate = onUpdate;
    const omega = (2 * Math.PI) / (opt.response ?? 0.35);
    this.stiffness = omega * omega;
    this.dampingKoeff = 2 * (opt.damping ?? 1) * omega;
  }

  /** Neues Ziel – die Bewegung läuft von hier weiter, mit Geschwindigkeit, ohne Sprung. */
  zu(ziel: number, opt: { geschwindigkeit?: number; sofort?: boolean; onSettle?: () => void } = {}): void {
    this.ziel = ziel;
    this.onSettle = opt.onSettle;
    if (opt.geschwindigkeit != null) this.geschwindigkeit = opt.geschwindigkeit;
    if (opt.sofort || reduzierteBewegung()) {
      this.stop();
      this.wert = ziel;
      this.geschwindigkeit = 0;
      this.onUpdate(ziel);
      this.onSettle?.();
      return;
    }
    if (!this.raf) {
      this.zuletzt = performance.now();
      this.raf = requestAnimationFrame(this.schritt);
    }
  }

  /** Anhalten, z. B. weil der Nutzer das Element gegriffen hat – der Wert bleibt, wo er ist. */
  stop(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  /** Direkt setzen (1:1 beim Ziehen). */
  setze(wert: number): void {
    this.stop();
    this.wert = wert;
    this.ziel = wert;
    this.onUpdate(wert);
  }

  private schritt = (jetzt: number): void => {
    let dt = Math.min((jetzt - this.zuletzt) / 1000, MAX_SCHRITT * 3);
    this.zuletzt = jetzt;
    // Feste Teilschritte halten die Integration stabil, auch bei Rucklern.
    while (dt > 0) {
      const h = Math.min(dt, MAX_SCHRITT);
      const kraft = -this.stiffness * (this.wert - this.ziel) - this.dampingKoeff * this.geschwindigkeit;
      this.geschwindigkeit += kraft * h;
      this.wert += this.geschwindigkeit * h;
      dt -= h;
    }
    const fertig = Math.abs(this.geschwindigkeit) < 0.01 && Math.abs(this.wert - this.ziel) < 0.01;
    if (fertig) {
      this.wert = this.ziel;
      this.geschwindigkeit = 0;
      this.raf = 0;
      this.onUpdate(this.wert);
      this.onSettle?.();
      return;
    }
    this.onUpdate(this.wert);
    this.raf = requestAnimationFrame(this.schritt);
  };
}

/** Ein Zahlenwert, der per Feder dem Ziel folgt – für Beträge, Maße, Positionen. */
export function useFeder(ziel: number, opt: FederOptionen = {}): number {
  const [wert, setWert] = useState(ziel);
  const feder = useRef<Feder | null>(null);
  if (!feder.current) feder.current = new Feder(ziel, setWert, opt);
  useEffect(() => {
    feder.current?.zu(ziel);
  }, [ziel]);
  useEffect(() => () => feder.current?.stop(), []);
  return wert;
}

/** Wo eine losgelassene Bewegung ausrollen würde (Apple-Projektion, wie beim Scrollen). */
export function projiziere(geschwindigkeit: number, deceleration = 0.998): number {
  return ((geschwindigkeit / 1000) * deceleration) / (1 - deceleration);
}

/** Weicher Widerstand jenseits einer Grenze statt hartem Anschlag. */
export function gummiband(ueberschuss: number, dimension: number, konstante = 0.55): number {
  return (ueberschuss * dimension * konstante) / (dimension + konstante * Math.abs(ueberschuss));
}

/** Geschwindigkeit aus den letzten Zeigerpositionen (px/s). */
export class Geschwindigkeitsmesser {
  private punkte: { t: number; p: number }[] = [];
  erfasse(p: number): void {
    const t = performance.now();
    this.punkte.push({ t, p });
    this.punkte = this.punkte.filter((x) => t - x.t < 100);
  }
  wert(): number {
    if (this.punkte.length < 2) return 0;
    const a = this.punkte[0]!;
    const b = this.punkte[this.punkte.length - 1]!;
    const dt = (b.t - a.t) / 1000;
    return dt > 0 ? (b.p - a.p) / dt : 0;
  }
  reset(): void {
    this.punkte = [];
  }
}
